import { useState } from "react";
import { RiCheckLine, RiTimeLine } from "react-icons/ri";
import { Text, View, XStack, YStack } from "tamagui";

import { colors } from "@styles/colors";

import { handleAPIJoinFamilyAfterPayment, type JoinFamilyAfterPaymentResult } from "../Account/API/handlePaymentAPI";

/** 後端錯誤不直接顯示，對應成中文提示 */
const mapJoinError = (raw: string, status?: number): string => {
  if (/NOT_PAID/.test(raw)) return "這筆還沒付款完成，付完款才能加入";
  if (/PAY_TOKEN/.test(raw)) return "付款連結已失效，沒辦法用這個連結加入";
  if (status === 401) return "請重新登入 LINE 後再試";
  if (status === 409 || status === 400) return "這個家庭目前沒辦法加入";
  return "送出失敗，請稍後再試";
};

/** 開關：與帳戶設定的 ToggleRow 同尺寸；帶 role="switch" 讓倒數知道使用者碰了它 */
const Switch = ({ label, value, onChange, disabled }: { label: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) => (
  <View
    w={44}
    h={26}
    br={13}
    bg={value ? colors.primary : colors.gray100}
    position="relative"
    cursor={disabled ? "default" : "pointer"}
    opacity={disabled ? 0.6 : 1}
    flexShrink={0}
    role="switch"
    aria-checked={value}
    aria-label={label}
    onPress={disabled ? undefined : () => onChange(!value)}
    style={{ transition: "background 0.2s" }}
  >
    <View
      w={22}
      h={22}
      br={11}
      bg={colors.light}
      position="absolute"
      t={2}
      l={value ? 20 : 2}
      style={{ transition: "left 0.2s", boxShadow: "0 2px 4px rgba(0,0,0,0.15)" }}
    />
  </View>
);

const SwitchRow = ({
  label,
  sub,
  value,
  onChange,
  disabled,
}: {
  label: string;
  sub?: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) => (
  <XStack ai="center" gap="$3">
    <YStack f={1} miw={0} gap={2}>
      <Text fontSize={14} fontWeight="700" color={colors.text}>
        {label}
      </Text>
      {sub ? (
        <Text fontSize={11.5} color={colors.sub}>
          {sub}
        </Text>
      ) : null}
    </YStack>
    <Switch label={label} value={value} onChange={onChange} disabled={disabled} />
  </XStack>
);

/**
 * 付款完成頁「加入家庭」（2026-10-28）：只給已註冊、但不是這張單家庭成員的人（viewer＝non_member）。
 * 打開「加入這個家庭」才出現「自動授權扣款」與確認按鈕；送出後隱藏開關、只顯示結果。
 * 直接加入或變成申請由負責人的「陌生訪客自動加入」決定（後端判斷）。
 */
export const JoinFamilyCard = ({ payToken, cusJwtToken }: { payToken: string; cusJwtToken: string }) => {
  const [join, setJoin] = useState(false);
  const [isPayer, setIsPayer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [result, setResult] = useState<(JoinFamilyAfterPaymentResult & { isPayer: boolean }) | null>(null);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setErrMsg("");
    const r = await handleAPIJoinFamilyAfterPayment(payToken, cusJwtToken, isPayer);
    setSubmitting(false);
    if (r.state === "success" && (r.data?.status === "active" || r.data?.status === "invited")) {
      setResult({ ...r.data, isPayer });
      return;
    }
    setErrMsg(r.state === "error" ? mapJoinError(r.error ?? "", r.status) : "送出失敗，請稍後再試");
  };

  const card = (children: React.ReactNode) => (
    <YStack
      width="100%"
      bg={colors.light}
      br={24}
      px={18}
      py={16}
      gap="$3"
      style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}
    >
      {children}
    </YStack>
  );

  if (result) {
    const active = result.status === "active";
    return card(
      <XStack ai="center" gap="$3">
        <View w={34} h={34} br={10} bg={active ? "#eafaf0" : colors.bgAmber} ai="center" jc="center" flexShrink={0}>
          {active ? <RiCheckLine size={19} color={colors.green} /> : <RiTimeLine size={19} color={colors.amberDark} />}
        </View>
        <YStack f={1} miw={0} gap={2}>
          <Text fontSize={14} fontWeight="800" color={colors.text}>
            {active ? "已加入這個家庭" : "已送出申請，等負責人核准後就會加入"}
          </Text>
          {active && result.isPayer ? (
            <Text fontSize={12} color={colors.sub}>
              之後可以直接付這個家庭的訂單
            </Text>
          ) : null}
        </YStack>
      </XStack>,
    );
  }

  return card(
    <>
      <SwitchRow label="加入這個家庭" value={join} onChange={setJoin} disabled={submitting} />
      {join ? (
        <>
          <YStack h={1} bg={colors.borderLight} />
          <SwitchRow
            label="自動授權扣款"
            sub="加入後可以直接付這個家庭的訂單"
            value={isPayer}
            onChange={setIsPayer}
            disabled={submitting}
          />
          {errMsg ? (
            <Text fontSize={12} color={colors.danger} role="alert">
              {errMsg}
            </Text>
          ) : null}
          <YStack
            br={12}
            py="$3"
            ai="center"
            className="primary-bg-gradient"
            cursor={submitting ? "default" : "pointer"}
            opacity={submitting ? 0.7 : 1}
            pressStyle={{ opacity: 0.85 }}
            role="button"
            aria-disabled={submitting}
            onPress={submit}
          >
            <Text fontSize={14.5} fontWeight="800" color={colors.light}>
              {submitting ? "送出中…" : errMsg ? "重新送出" : "確認加入"}
            </Text>
          </YStack>
        </>
      ) : null}
    </>,
  );
};

export default JoinFamilyCard;
